import React, { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  path?: string;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SITE_URL = 'https://jk-automations.com';
const JSON_LD_ID = 'page-jsonld';

const setMetaTag = (selector: string, attrs: Record<string, string>) => {
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!tag) {
    tag = document.createElement('meta');
    document.head.appendChild(tag);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    tag?.setAttribute(key, value);
  });
};

const PageMeta: React.FC<PageMetaProps> = ({ title, description, path = '/', structuredData }) => {
  useEffect(() => {
    document.title = title;

    setMetaTag('meta[name="description"]', { name: 'description', content: description });
    setMetaTag('meta[name="title"]', { name: 'title', content: title });
    setMetaTag('meta[property="og:title"]', { property: 'og:title', content: title });
    setMetaTag('meta[property="og:description"]', { property: 'og:description', content: description });
    setMetaTag('meta[property="twitter:title"]', { property: 'twitter:title', content: title });
    setMetaTag('meta[property="twitter:description"]', { property: 'twitter:description', content: description });

    const pageUrl = `${SITE_URL}${path}`;
    setMetaTag('meta[property="og:url"]', { property: 'og:url', content: pageUrl });
    setMetaTag('meta[property="twitter:url"]', { property: 'twitter:url', content: pageUrl });

    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = pageUrl;

    const existingScript = document.getElementById(JSON_LD_ID);
    if (existingScript) {
      existingScript.remove();
    }

    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSON_LD_ID;
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      const currentScript = document.getElementById(JSON_LD_ID);
      if (currentScript) {
        currentScript.remove();
      }
    };
  }, [description, path, structuredData, title]);

  return null;
};

export default React.memo(PageMeta);
